"use client";

// import { Chat } from '@/lib/db/schema';
import { useEffect, useState, memo, useRef } from "react";
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from "./ui/sidebar";
import Link from "next/link";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  MoreHorizontalIcon,
  ShareIcon,
  TrashIcon,
  DownloadIcon,
  EyeIcon,
  PencilEditIcon,
} from "./icons";
import { toast } from "sonner";
import { useRouter } from "next/navigation";
import { useSelector, useDispatch } from "react-redux";
import { setActiveItem, setGlobalLoading } from "@/store/mainSlice";
import { ShareLinkPopup } from "./share-link-popup";
import { handleView, handleDownload, downloadCardFromURL, downloadPDF} from "@/lib/utils";
import BuyCreditsModal from "./BuyCreditsModal";


function capitalizeFirstLetter(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const PureChatItem = ({
  item,
  isActive,
  onDelete,
  setOpenMobile,
  setHistory,
  renamingId,
  setRenamingId
}) => {

  const [shareOpen, setShareOpen] = useState(false);
  const [generateLinkData, setGenerateLinkData] = useState(null);
  const router = useRouter();
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [showRenameInput, setShowRenameInput] = useState(false);
  const [newTitle, setNewTitle] = useState(item.title || "");
  const inputRef = useRef(null);
  const { userData, isFreeLimit, isGeneratingWebsite, activeTab } = useSelector((state) => state.main)
  const dispatch = useDispatch();
  const userId1 = userData?._id;
  const screenIdKey = item?.screen_id ? String(item.screen_id) : "";
  const isRedesign = activeTab === "Page Builder" && (
    Boolean(item?.redesignPrompt) ||
    (item?.prompt && item.prompt.toLowerCase().startsWith("redesign the website at")) ||
    Boolean(JSON.parse(localStorage.getItem("redesignFlags") || "{}")[screenIdKey])
  );

  useEffect(() => {
    if (!item?.screen_id || !isRedesign) return;
    const redesignFlags = JSON.parse(localStorage.getItem("redesignFlags") || "{}");
    if (!redesignFlags[screenIdKey]) {
      redesignFlags[screenIdKey] = true;
      localStorage.setItem("redesignFlags", JSON.stringify(redesignFlags));
    }
  }, [isRedesign, screenIdKey, item?.screen_id]);

  useEffect(() => {
    setShareOpen(true);
  }, [generateLinkData]);

  const handleDelete = async () => {
    try {
      if (isGeneratingWebsite) {
        toast.error("Please hold on—we’re setting up your page.", {
          style: { color: "red" },
          position: "top-center",
          iconTheme: { primary: "red", secondary: "#fff" },
        });
        return;
      }
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website/chats/${item.screen_id}`,
        {
          method: "DELETE",
        }
      );
      if (!res.ok) {
        throw new Error("Failed to delete chat");
      }

      onDelete(item.screen_id);
      toast.error("Chat Deletion Successful.", {
        style: { color: "green" },
        position: "top-center",
      });

      // Only redirect to home if we're deleting the currently active chat
      if (isActive) {
        window.dispatchEvent(new CustomEvent("chatDeleted", {
          detail: { screenId: item.screen_id }
        }));
        router.replace("/");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Unable to delete chat. Please try again.", {
        style: { color: "red" },
        position: "top-center",
      });
    }
  };


  const handleRenameM = async () => {
    try {
      if (!newTitle.trim()) {
        toast.error("Title cannot be empty", {
          position: "top-center",
        });
        return false;
      }

      const apiUrl = `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website/rename/${item.screen_id}`;
      // console.log("Calling rename API:", apiUrl);
      // console.log("Request body:", { title: newTitle });

      const res = await fetch(apiUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("API Error response:", errorText);
        throw new Error(`Failed to rename chat: ${res.status} - ${errorText}`);
      }

      const data = await res.json();
      // console.log("API Response data:", data);
      // console.log("Original item title:", item.title);
      // console.log("New title being set:", newTitle);

      if (data.status === 1) {
        toast.success("Title updated!", {
          style: { color: "green" },
          position: "top-center",
        });


        setHistory((prev) =>
          prev.map((h) =>
            h.screen_id === item.screen_id ? { ...h, title: newTitle } : h
          )
        );

        // Update the item prop directly for immediate UI update
        item.title = newTitle;
        setShowRenameInput(false);


        // Store the rename in localStorage as backup
        const renameBackup = JSON.parse(localStorage.getItem('chatRenames') || '{}');
        renameBackup[item.screen_id] = newTitle;
        localStorage.setItem('chatRenames', JSON.stringify(renameBackup));

        // Dispatch custom event to update other components
        window.dispatchEvent(new CustomEvent("chatRenamed", {
          detail: { screenId: item.screen_id, newTitle }
        }));
        return true; // Return success status
      } else {
        console.error("API returned status 0:", data.message);
        toast.error(data.message || "Failed to update title", {
          style: { color: "red" },
          position: "top-center",
        });
        return false;
      }
    } catch (err) {
      console.error("Rename error:", err);
      toast.error(`Unable to rename. Error: ${err.message}`, {
        style: { color: "red" },
        position: "top-center",
      });
      return false;
    }
  };
  const handleRename = async () => {
    try {
      if (!newTitle.trim()) {
        toast.error("Title cannot be empty", { position: "top-center" });
        return false;
      }

      const apiUrl = `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website/rename/${item.screen_id}`;
      // console.log("Calling rename API:", apiUrl);
      // console.log("Request body:", { title: newTitle });

      const res = await fetch(apiUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("API Error response:", errorText);
        throw new Error(`Failed to rename chat: ${res.status} - ${errorText}`);
      }

      const data = await res.json();
      // console.log("API Response data:", data);

      if (data.status === 1) {
        toast.success("Title updated!", {
          style: { color: "green" },
          position: "top-center",
        });

        // ✅ Update sidebar list instantly
        setHistory((prev) =>
          prev.map((h) =>
            h.screen_id === item.screen_id ? { ...h, title: newTitle } : h
          )
        );

        // ✅ Directly update prop object for immediate visible change
        // item.title = newTitle;
        setShowRenameInput(false);
        setRenamingId(null);

        // ✅ Store rename backup (for cases where API fetch overwrites)
        const renameBackup =
          JSON.parse(localStorage.getItem("chatRenames") || "{}");
        renameBackup[item.screen_id] = newTitle;
        localStorage.setItem("chatRenames", JSON.stringify(renameBackup));

        // ✅ Dispatch rename event but DO NOT re-fetch immediately
        window.dispatchEvent(
          new CustomEvent("chatRenamed", {
            detail: { screenId: item.screen_id, newTitle },
          })
        );

        return true;
      } else {
        console.error("API returned status 0:", data.message);
        toast.error(data.message || "Failed to update title", {
          style: { color: "red" },
          position: "top-center",
        });
        return false;
      }
    } catch (err) {
      console.error("Rename error:", err);
      toast.error(`Unable to rename. Error: ${err.message}`, {
        style: { color: "red" },
        position: "top-center",
      });
      return false;
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleRename();
    if (e.key === "Escape") {
      setShowRenameInput(false);
      setNewTitle(item.title || "");
      setOpenMobile(false);
    }
  };
  useEffect(() => {
    const handleOutsideMouseDown = (e) => {
      if (
        showRenameInput &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setTimeout(() => {
          setShowRenameInput(false);
          setRenamingId(null);
        }, 150);

      }
    };

    window.addEventListener("mousedown", handleOutsideMouseDown);
    return () => {
      window.removeEventListener("mousedown", handleOutsideMouseDown);
    };
  }, [showRenameInput]);

  return (
    <SidebarMenuItem>
      {showRenameInput ? (
        <input
          id={`rename-input-${item.screen_id}`}
          ref={inputRef}
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          // onFocus={() => { }}
          onBlur={async () => {
            if (
              newTitle.trim() !== (item.title || "").trim() &&
              newTitle.trim() !== ""
            ) {
              const success = await handleRename();
              if (!success) {
                setNewTitle(item.title || "");
              }
            } else {
              setNewTitle(item.title || "");
            }

            setShowRenameInput(false);
            setRenamingId(null);
            setOpenMobile(false);
          }}
          className="border rounded px-2 py-1 w-full"
        />
      ) : (
        <SidebarMenuButton asChild isActive={isActive}>
          <Link
            href="#"
            onClick={async (e) => {
              e.preventDefault();
              if (isGeneratingWebsite) {
                toast.error("Please hold on—we’re setting up your page.", {
                  style: { color: "red" },
                  position: "top-center",
                  iconTheme: { primary: "red", secondary: "#fff" },
                });
                return;
              }
              setOpenMobile(false);
              dispatch(setGlobalLoading(true))
              await router.push(`/${item.screen_id}`);
            }}
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className="truncate">
                {item.title && item.title.trim() !== "" && item.title.trim() !== "Untitled"
                  ? capitalizeFirstLetter(item.title.trim())
                  : item.prompt && item.prompt.trim() !== ""
                    ? capitalizeFirstLetter(item.prompt.trim())
                    : "Untitled Conversation"}
              </span>
              {isRedesign && (
                <span className="shrink-0 rounded-full border border-zinc-300/80 bg-zinc-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-zinc-600 dark:border-white/10 dark:bg-white/5 dark:text-white/70">
                  Re-design
                </span>
              )}
            </span>
          </Link>
        </SidebarMenuButton>
      )}

      <DropdownMenu modal={true}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground mr-0.5"
            showOnHover={!isActive}
          >
            <MoreHorizontalIcon />
            <span className="sr-only">More</span>
          </SidebarMenuAction>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="bottom" align="end">
          {activeTab == "Page Builder" && (
            <DropdownMenuItem
              className="cursor-pointer focus:bg-accent focus:text-accent-foreground"
              onClick={() => {
                dispatch(setActiveItem("z-[45]"));

                if (isGeneratingWebsite) {
                  toast.error("Please hold on—we’re setting up your page.", {
                    style: { color: "red" },
                    position: "top-center",
                    iconTheme: { primary: "red", secondary: "#fff" },
                  });
                  return;
                }
                setShareOpen(false);
                let generateData = {
                  websiteUrl: item.html_file_detail,
                  screenId: item.screen_id,
                  websiteDetailsUid: item._id,
                  userId: userId1,
                };
                console.log(generateData, "-----generateDatagenerateDatagenerateData")
                setGenerateLinkData(generateData);
              }}
            >
              <ShareIcon />
              <span>Share Public Link</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="cursor-pointer focus:bg-accent focus:text-accent-foreground"
            onClick={() => {
              if (isFreeLimit) {
                dispatch(setActiveItem("z-[45]"));
                setShowBuyCreditsModal(true);
                return;
              }
              if (activeTab === "Card Maker") {
                downloadCardFromURL(item.html_file_detail, item.screen_id)
              } else if (activeTab === "Page Builder") {
                handleDownload(item.html_file_detail, item.screen_id);
              } else {
                // downloadBusinessPlanPDF(item.content, item.screen_id)
                downloadPDF(item.content, item.screen_id)
              }
            }}
          >
            <DownloadIcon />
            <span>Download</span>
          </DropdownMenuItem>

          {activeTab == "Page Builder" && (
            <DropdownMenuItem
              className="cursor-pointer focus:bg-accent focus:text-accent-foreground"
              onClick={() => {
                if (isFreeLimit) {
                  dispatch(setActiveItem("z-[45]"));
                  setShowBuyCreditsModal(true);
                  return;
                }
                setOpenMobile(false);
                handleView(item.html_file_detail);
              }}
            >
              <EyeIcon />
              <span>Preview</span>
            </DropdownMenuItem>
          )}

          <DropdownMenuItem
            className="cursor-pointer focus:bg-accent focus:text-accent-foreground"
            onClick={() => {
              setRenamingId(item.screen_id);
              setShowRenameInput(true);
              setTimeout(() => {
                const inputEl = document.getElementById(`rename-input-${item.screen_id}`);
                if (inputEl) {
                  // inputEl.focus();
                  inputEl.select();
                }
              }, 500);
            }}
          >
            <PencilEditIcon />
            <span>Rename</span>
          </DropdownMenuItem>
          {/* <DropdownMenuItem
            className="cursor-pointer text-destructive focus:bg-destructive/15 focus:text-destructive dark:text-red-500"
            onClick={handleDelete}
          >
            <TrashIcon />
            <span>Delete</span>
          </DropdownMenuItem> */}
        </DropdownMenuContent>
      </DropdownMenu>

      {generateLinkData && shareOpen && userData && (
        <ShareLinkPopup
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          // isFreeLimit={userData.is_free_limit}
          generateData={generateLinkData}
          isWebsiteHistoryPage={true}
          userDetail={userData}
        />
      )}

      {showBuyCreditsModal && userData && (
        <BuyCreditsModal
          show={showBuyCreditsModal}
          handleClose={() => setShowBuyCreditsModal(false)}
          userDetail={userData}
        />
      )}
    </SidebarMenuItem>
  );
};

export const ChatItem = memo(PureChatItem, (prevProps, nextProps) => {
  return (
    prevProps.isActive === nextProps.isActive &&
    JSON.stringify(prevProps.item) === JSON.stringify(nextProps.item)
  );
});
